import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { usajiliApi } from '@services/api/usajili';
import { apiClient, OtpChallenge } from '@services/api/client';
import {
  USAJILIRole,
  USAJILIMetadata,
  USAJILIFilterState,
  Officer,
  USAJILI_NOT_CAPTURED,
} from '@types';

/* ================================================================
   Context shape
================================================================= */
interface USAJILIContextValue {
  loading: boolean;
  error: string | null;
  role: USAJILIRole | null;
  officer: Officer | null;
  isAdmin: boolean;
  isHQ: boolean;
  metadata: USAJILIMetadata | null;
  filters: USAJILIFilterState;
  setFilters: React.Dispatch<React.SetStateAction<USAJILIFilterState>>;
  updateFilter: <K extends keyof USAJILIFilterState>(k: K, v: USAJILIFilterState[K]) => void;
  resetFilters: () => void;
  /** Resolves to a challenge when the account needs an emailed code
   *  (then call verifyOtp), or null once signed in. */
  login: (email: string, password: string) => Promise<OtpChallenge | null>;
  verifyOtp: (otpToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMetadata: () => Promise<void>;
  refetchKey: number;
  bumpRefetch: () => void;
}

const DEFAULT_FILTERS: USAJILIFilterState = {
  search: '',
  date_from: '',
  date_to: '',
  county: 'All',
  district: 'All',
  division: 'All',
  location: 'All',
};

const Ctx = createContext<USAJILIContextValue | null>(null);

export const useUSAJILI = (): USAJILIContextValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useUSAJILI must be used inside USAJILIProvider');
  return v;
};

/* ================================================================
   Provider
================================================================= */
export const USAJILIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<USAJILIRole | null>(null);
  const [officer, setOfficer] = useState<Officer | null>(null);
  const [metadata, setMetadata] = useState<USAJILIMetadata | null>(null);
  const [filters, setFilters] = useState<USAJILIFilterState>(DEFAULT_FILTERS);
  const [refetchKey, setRefetchKey] = useState<number>(0);

  const bumpRefetch = useCallback(() => setRefetchKey((k) => k + 1), []);

  const updateFilter = useCallback(
    <K extends keyof USAJILIFilterState>(k: K, v: USAJILIFilterState[K]) => {
      setFilters((prev) => {
        const next = { ...prev, [k]: v };
        // Reset cascading children when parent changes
        if (k === 'county') {
          next.district = 'All';
          next.division = 'All';
          next.location = 'All';
        } else if (k === 'district') {
          next.division = 'All';
          next.location = 'All';
        } else if (k === 'division') {
          next.location = 'All';
        }
        return next;
      });
    },
    []
  );

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const loadMeAndMeta = useCallback(async () => {
    // No token → no session, stay on login page without triggering the
    // axios refresh interceptor (which would redirect to /login on 401).
    const token = localStorage.getItem('sdics_access_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const me = await usajiliApi.me();
      setRole(me.role);
      setOfficer(me.officer);
      try {
        const m = await usajiliApi.metadata();
        setMetadata(m);
      } catch {
        // metadata non-fatal; fall back to skeleton with counties only
        setMetadata({
          counties: [
            'Baringo', 'Bomet', 'Bungoma', 'Elgeyo Marakwet', 'Kericho',
            'Nakuru', 'Nandi', 'Narok', 'Samburu', 'Trans Nzoia',
            'Turkana', 'Uasin Gishu', 'West Pokot',
          ],
          districts_by_county: {},
          divisions_by_key: {},
          locations_by_key: {},
          not_captured_placeholder: USAJILI_NOT_CAPTURED,
        });
      }
    } catch (e: any) {
      setError(e?.error?.message || e?.message || 'Failed to load USAJILI session');
      setRole(null);
      setOfficer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMeAndMeta();
  }, [loadMeAndMeta]);

  // After the SDICS core admin-login succeeds, load the usajili role. If
  // that fails (no usajili_user row), surface that error but keep the
  // SDICS session so the message is precise.
  const loadRoleAfterLogin = useCallback(async () => {
    try {
      const me = await usajiliApi.me();
      setRole(me.role);
      setOfficer(me.officer);
      try {
        setMetadata(await usajiliApi.metadata());
      } catch {
        /* ignore meta failure */
      }
    } catch (uErr: any) {
      const msg = uErr?.error?.message || uErr?.message || 'This account does not have USAJILI access';
      setError(msg);
      setRole(null);
      setOfficer(null);
      throw new Error(msg);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const res = await apiClient.adminLogin(email, password);
      if (res.otpRequired) return res.challenge;
      await loadRoleAfterLogin();
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadRoleAfterLogin]);

  const verifyOtp = useCallback(async (otpToken: string, code: string) => {
    setError(null);
    setLoading(true);
    try {
      await apiClient.verifyAdminOtp(otpToken, code);
      await loadRoleAfterLogin();
    } finally {
      setLoading(false);
    }
  }, [loadRoleAfterLogin]);

  const logout = useCallback(async () => {
    try {
      await apiClient.logout();
    } catch {
      /* ignore */
    }
    setRole(null);
    setOfficer(null);
    setError(null);
    setFilters(DEFAULT_FILTERS);
  }, []);

  const refreshMetadata = useCallback(async () => {
    try {
      setMetadata(await usajiliApi.metadata());
    } catch {
      /* ignore */
    }
  }, []);

  // Data changed (upload / add / edit / delete): reload metadata so the
  // header's data range and "last updated" time stay current.
  useEffect(() => {
    if (refetchKey > 0) void refreshMetadata();
  }, [refetchKey, refreshMetadata]);

  const value = useMemo<USAJILIContextValue>(
    () => ({
      loading,
      error,
      role,
      officer,
      isAdmin: role === 'ADMIN',
      isHQ: role === 'HQ',
      metadata,
      filters,
      setFilters,
      updateFilter,
      resetFilters,
      login,
      verifyOtp,
      logout,
      refreshMetadata,
      refetchKey,
      bumpRefetch,
    }),
    [loading, error, role, officer, metadata, filters, updateFilter, resetFilters, login, verifyOtp, logout, refreshMetadata, refetchKey, bumpRefetch]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
